import { HTMLProps, useEffect, useState } from "react";
import {
  Identifier,
  ReactAdminComponentProps,
  ReferenceInput,
  AutocompleteInput,
  Create,
  SimpleForm,
  useListContext,
  SelectInput,
  required,
} from "react-admin";
import { Drawer, withStyles } from "@material-ui/core";
import { EditActions } from "../../common/form/FormActions";
import resourceNames from "../../../common/data/resourceNames";
import { RouteComponentProps } from "react-router-dom";
import { useSearchFields } from "../../../common/hooks/useSearchFields";
import { BulkAssignFormInit } from "./BulkAssignFormInit";
import { VehicleViewApiFactory } from "../../../common/data/service-proxies";
import { useAxiosInstance } from "../../../common/hooks/useAxiosInstance";
import { useForm } from "react-final-form";
import { allowedUnidentifiedDutyStatusChoices } from "./helpers";

interface Props extends RouteComponentProps {
  selectedIds: Array<Identifier>;
  onClose: () => void;
}

const AutocompleteInputInDialog = withStyles({
  suggestionsContainer: { zIndex: 2000 },
})(AutocompleteInput);

export const BulkAssignUnidentifiedDrivingForm = ({
  selectedIds,
  onClose,
  ...rest
}: Props & ReactAdminComponentProps & HTMLProps<HTMLDivElement>) => {
  useSearchFields(rest, ["driverName"], resourceNames.ADMIN_DRIVERS);
  const axios = useAxiosInstance();
  const { data } = useListContext();
  const [driverId, setDriverId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const getDriverId = async (vehicleId: string) => {
    if (!axios) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await VehicleViewApiFactory(
        undefined,
        axios.defaults.baseURL,
        axios
      ).apiAppVehicleviewVehiclecurrentdriverVehicleIdGet(Number(vehicleId));

      setDriverId(response.data);
    } catch (error) {
      console.error("Error fetching driver ID:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedIds || !selectedIds.length || !data) {
      return;
    }

    const selectedVehicleIds = selectedIds
      .map((id) => data[id]?.vehicleId)
      .filter(Boolean);

    const allSameVehicle = new Set(selectedVehicleIds).size === 1;

    if (allSameVehicle) getDriverId(selectedVehicleIds[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, data]);

  return (
    <Drawer
      anchor="right"
      open
      onClose={() => {
        onClose();
      }}
    >
      <Create
        resource={rest.resource}
        basePath={rest.basePath}
        actions={<EditActions />}
        onSuccess={() => {
          onClose();
        }}
        title=" "
      >
        <SimpleForm variant="outlined">
          <SetDriverIdEffect driverId={driverId} />

          <ReferenceInput
            source="driverId"
            label="resources.hos-unidentified-driving.labels.driver"
            reference={resourceNames.ADMIN_DRIVERS}
          >
            <AutocompleteInputInDialog
              disabled={isLoading}
              optionText="driverName"
              translateChoice={false}
            />
          </ReferenceInput>
          <SelectInput
            defaultValue="D"
            source="dutyStatus"
            choices={allowedUnidentifiedDutyStatusChoices}
            validate={required()}
          />
          <BulkAssignFormInit selectedIds={selectedIds} />
        </SimpleForm>
      </Create>
    </Drawer>
  );
};

const SetDriverIdEffect = ({ driverId }: { driverId: number | null }) => {
  const form = useForm();

  useEffect(() => {
    if (form && driverId !== null) {
      form.change("driverId", driverId);
    }
  }, [driverId, form]);

  return null;
};
